import { dbService } from './databaseService';
import { User, UserPreferences, UserStats } from '../types';
import bcryptjs from 'bcryptjs';

// Extended User type with passwordHash for auth purposes
interface AuthUser extends User {
  passwordHash?: string;
}

// Authentication Service
export class AuthService {
  private static instance: AuthService;

  private constructor() {}

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Register a new user
   */
  public async register(username: string, email: string, password: string, country: string = 'US'): Promise<User | null> {
    try {
      // Check if email is already in use
      const existingUser = await dbService.getUserByEmail(email);
      if (existingUser) {
        throw new Error('Email is already in use');
      }

      // Hash the password
      const passwordHash = await this.hashPassword(password);
      
      // Create default user preferences and stats
      const preferences: UserPreferences = {
        theme: 'default',
        font: 'mono',
        fontSize: 16,
        smoothCaret: true,
        soundEnabled: false,
        blindMode: false,
        keymap: 'qwerty',
      };
      
      const stats: UserStats = {
        totalTests: 0,
        avgWpm: 0,
        bestWpm: 0,
        avgAccuracy: 0,
        bestAccuracy: 0,
        totalTimeTyping: 0,
        streak: 0,
        bestStreak: 0,
        battlesWon: 0,
        battlesLost: 0,
      };
      
      // Create the user with XataUser properties
      const newXataUser = await dbService.createUser({
        username,
        email,
        passwordHash,
        level: 1,
        xp: 0,
        totalXp: 0,
        isPremium: false,
        country,
        preferences,
        stats,
        achievements: [],
        recentActivity: [],
        createdAt: new Date().toISOString(),
      } as any); // Cast to any to handle passwordHash
      
      if (!newXataUser) {
        throw new Error('Failed to create user');
      }
      
      // Create a clean user object without passwordHash
      const newUser: User = {
        ...newXataUser,
        achievements: newXataUser.achievements || [],
        recentActivity: newXataUser.recentActivity || []
      };
      
      return newUser;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Login a user
   */
  public async login(email: string, password: string): Promise<User | null> {
    try {
      // Get user by email
      const user = await dbService.getUserByEmail(email) as AuthUser;
      if (!user) {
        throw new Error('Invalid email or password');
      }
      
      // Use XataDB to get the raw user data with passwordHash
      const userWithPassword = await dbService.getUserByEmailWithPassword(email);
      if (!userWithPassword?.passwordHash) {
        throw new Error('Invalid login method');
      }
      
      // Verify password
      const isPasswordValid = await this.comparePassword(password, userWithPassword.passwordHash);
      if (!isPasswordValid) {
        throw new Error('Invalid email or password');
      }
      
      return {
        ...user,
        achievements: user.achievements || [],
        recentActivity: user.recentActivity || []
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Login or register with OAuth
   */
  public async oauthLogin(provider: string, email: string, username: string): Promise<User | null> {
    try {
      // Check if the user exists
      let user = await dbService.getUserByEmail(email);
      
      if (!user) {
        // Create default user preferences and stats
        const preferences: UserPreferences = {
          theme: 'default',
          font: 'mono',
          fontSize: 16,
          smoothCaret: true,
          soundEnabled: false,
          blindMode: false,
          keymap: 'qwerty',
        };
        
        const stats: UserStats = {
          totalTests: 0,
          avgWpm: 0,
          bestWpm: 0,
          avgAccuracy: 0,
          bestAccuracy: 0,
          totalTimeTyping: 0,
          streak: 0,
          bestStreak: 0,
          battlesWon: 0,
          battlesLost: 0,
        };
        
        // Create new user if they don't exist
        const newUser = await dbService.createUser({
          username,
          email,
          level: 1,
          xp: 0,
          totalXp: 0,
          isPremium: false,
          country: 'US', // Default country
          preferences,
          stats,
          achievements: [],
          recentActivity: [],
          createdAt: new Date().toISOString(),
        } as any);
        
        if (!newUser) {
          throw new Error(`Failed to create user with ${provider}`);
        }
        
        user = newUser;
      }
      
      return {
        ...user,
        achievements: user.achievements || [],
        recentActivity: user.recentActivity || []
      };
    } catch (error) {
      console.error(`OAuth login error (${provider}):`, error);
      throw error;
    }
  }

  /**
   * Hash a password
   */
  private async hashPassword(password: string): Promise<string> {
    return await bcryptjs.hash(password, 10);
  }

  /**
   * Compare a password with a hash
   */
  private async comparePassword(password: string, hash: string): Promise<boolean> {
    return await bcryptjs.compare(password, hash);
  }
}

// Export a singleton instance
export const authService = AuthService.getInstance();
